import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import {
  findByPermalink,
  galleryItemsQueryOptions,
  itemTitle,
  type GalleryItem,
} from "@/lib/gallery.queries";
import { VideoLightbox } from "@/components/site/VideoLightbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/beauty-show/$permalink")({
  loader: async ({ context, params }) => {
    const items = await context.queryClient.ensureQueryData(
      galleryItemsQueryOptions("beauty-episode"),
    );
    const item = findByPermalink(items, params.permalink);
    if (!item) throw notFound();
    return { item };
  },
  head: ({ loaderData }) => {
    const item = loaderData?.item as GalleryItem | undefined;
    if (!item) return {};
    const title = `${itemTitle(item, "Episode")} — The Beauty Show — Medical Insight`;
    const description =
      item.description?.slice(0, 200) ?? "An episode of The Beauty Show from Medical Insight.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        ...(item.poster_path ? [{ property: "og:image", content: item.poster_path }] : []),
      ],
    };
  },
  notFoundComponent: EpisodeNotFound,
  component: BeautyShowEpisodeRoute,
});

function BeautyShowEpisodeRoute() {
  const { item } = Route.useLoaderData();
  const navigate = useNavigate();

  return (
    <VideoLightbox
      item={item}
      open
      basePath="/beauty-show"
      kicker="The Beauty Show"
      onOpenChange={(open) => {
        if (!open) {
          navigate({
            to: "/beauty-show",
            search: (prev) => ({ ...prev, video: undefined }),
            replace: true,
          });
        }
      }}
    />
  );
}

function EpisodeNotFound() {
  const navigate = useNavigate();
  const back = () =>
    navigate({
      to: "/beauty-show",
      search: (prev) => ({ ...prev, video: undefined }),
      replace: true,
    });
  return (
    <Dialog open onOpenChange={(open) => !open && back()}>
      <DialogContent className="max-w-md">
        <DialogHeader className="pr-10 text-left">
          <DialogTitle>Episode not found</DialogTitle>
          <DialogDescription>
            This episode is no longer available or the link is incorrect.
          </DialogDescription>
        </DialogHeader>
        <Button onClick={back}>Back to The Beauty Show</Button>
      </DialogContent>
    </Dialog>
  );
}
